<script lang="ts">
    import { selectAndGotoTab, type TabClass } from "@store/pageTracker"
    import { createEventDispatcher } from "svelte"
    import EditableTabField from "./EditableTabField.svelte"

    export let tab: TabClass
    export let selected = false
    export let collapsed = false

    const dispatch = createEventDispatcher<{
        select: void
        tooltipshow: { title: string; top: number; left: number }
        tooltiphide: void
    }>()

    $: title = tab.displayName
    $: untitled = !tab.nameWithoutEmoji
    $: locationLabel = tab.path
    $: tabInitial = title.trim().charAt(0).toLocaleUpperCase() || "?"

    function selectTab() {
        dispatch("tooltiphide")
        selectAndGotoTab(tab.id)
        dispatch("select")
    }

    function handleKeydown(event: KeyboardEvent) {
        if (event.target !== event.currentTarget) return
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        selectTab()
    }

    function showCollapsedTooltip(event: MouseEvent | FocusEvent) {
        if (!collapsed) return

        const node = event.currentTarget as HTMLElement | null
        if (!node) return

        const rect = node.getBoundingClientRect()
        dispatch("tooltipshow", {
            title,
            top: rect.top + rect.height / 2,
            left: rect.right + 12,
        })
    }

    function hideCollapsedTooltip() {
        dispatch("tooltiphide")
    }
</script>

<div
    class="reading-tab-item"
    class:selected
    class:collapsed
    role="button"
    tabindex="0"
    aria-current={selected ? "page" : undefined}
    aria-label={`${selected ? "Current reading tab" : "Open reading tab"}: ${title}`}
    on:click={selectTab}
    on:keydown={handleKeydown}
    on:mouseenter={showCollapsedTooltip}
    on:mouseleave={hideCollapsedTooltip}
    on:focus={showCollapsedTooltip}
    on:blur={hideCollapsedTooltip}
>
    <span class="tab-icon" aria-hidden="true">
        {#if tab.emoji}
            <span class="tab-emoji">{tab.emoji}</span>
        {:else}
            <span class="tab-initial">{tabInitial}</span>
        {/if}
    </span>

    <span class="tab-copy">
        <span class="tab-title" class:untitled={!selected && untitled}>
            {#if selected && !collapsed}
                <EditableTabField {tab} kind="name" displayValue={title} />
            {:else}
                {title}
            {/if}
        </span>
        <span class="tab-location">
            {#if selected && !collapsed}
                <EditableTabField {tab} kind="path" />
            {:else}
                {locationLabel}
            {/if}
        </span>
    </span>
</div>

<style>
    .reading-tab-item {
        appearance: none;
        position: relative;
        display: grid;
        grid-template-columns: 2rem minmax(0, 1fr);
        align-items: center;
        gap: 0.6rem;
        width: calc(100% - var(--reader-tabs-inactive-end-gap, 0.45rem));
        min-width: 0;
        min-height: 2.75rem;
        border: 1px solid var(--reader-tabs-tab-border, var(--reader-tabs-border));
        border-right-color: var(--reader-tabs-tab-border, var(--reader-tabs-border));
        border-radius: 0.62rem;
        box-shadow: none;
        background: var(--reader-tabs-tab-bg, transparent);
        color: var(--reader-tabs-text);
        cursor: pointer;
        font: inherit;
        letter-spacing: 0;
        padding: 0.28rem 0.4rem;
        text-align: left;
        text-transform: none;
        transition:
            background-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
    }

    .reading-tab-item:hover {
        border-color: var(--reader-tabs-tab-hover-border, var(--reader-tabs-active-border));
        border-right-color: var(--reader-tabs-tab-hover-border, var(--reader-tabs-active-border));
        background: var(--reader-tabs-hover-bg);
        color: var(--reader-tabs-strong);
    }

    .reading-tab-item:active {
        transform: translateY(1px);
    }

    .reading-tab-item:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .reading-tab-item.selected {
        z-index: 2;
        width: 100%;
        border-color: var(--reader-tabs-active-border);
        border-right-color: var(--reader-tabs-active-bg);
        border-radius: 0.62rem 0 0 0.62rem;
        background: var(--reader-tabs-active-bg);
        color: var(--reader-tabs-active-text);
    }

    .reading-tab-item.selected::before,
    .reading-tab-item.selected::after {
        content: "";
        position: absolute;
        right: calc(-1 * var(--reader-tabs-panel-border-width, 1px));
        width: var(--reader-tabs-edge-fill, 0.58rem);
        height: var(--reader-tabs-edge-fill, 0.58rem);
        pointer-events: none;
    }

    .reading-tab-item.selected::before {
        top: calc(-1 * var(--reader-tabs-edge-fill, 0.58rem));
        border-right: 1px solid var(--reader-tabs-active-border);
        border-bottom: 1px solid var(--reader-tabs-active-border);
        border-bottom-right-radius: var(--reader-tabs-edge-fill, 0.58rem);
        box-shadow: 0.24rem 0.24rem 0 var(--reader-tabs-active-bg);
    }

    .reading-tab-item.selected::after {
        bottom: calc(-1 * var(--reader-tabs-edge-fill, 0.58rem));
        border-top: 1px solid var(--reader-tabs-active-border);
        border-right: 1px solid var(--reader-tabs-active-border);
        border-top-right-radius: var(--reader-tabs-edge-fill, 0.58rem);
        box-shadow: 0.24rem -0.24rem 0 var(--reader-tabs-active-bg);
    }

    .tab-icon {
        display: grid;
        place-items: center;
        width: 1.85rem;
        height: 1.85rem;
        border-radius: 0.45rem;
        background: var(--reader-tabs-icon-bg);
        color: var(--reader-tabs-icon-text);
        font-size: 1rem;
        line-height: 1;
    }

    .reading-tab-item.selected .tab-icon {
        background: var(--reader-tabs-active-icon-bg);
        color: var(--reader-tabs-active-icon-text);
    }

    .tab-icon :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .tab-emoji {
        display: block;
        max-width: 1.4rem;
        overflow: hidden;
        text-align: center;
        text-overflow: clip;
        white-space: nowrap;
    }

    .tab-initial {
        display: block;
        font-size: 0.8rem;
        font-weight: 820;
        line-height: 1;
        text-transform: uppercase;
    }

    .tab-copy {
        display: grid;
        gap: 0.22rem;
        min-width: 0;
    }

    .tab-title,
    .tab-location {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .tab-title {
        color: var(--reader-tabs-strong);
        font-size: 0.84rem;
        font-weight: 760;
        line-height: 1.1;
    }

    .tab-title.untitled {
        font-style: italic;
    }

    .tab-location {
        color: var(--reader-tabs-muted);
        font-size: 0.72rem;
        font-weight: 650;
        line-height: 1.1;
    }

    .reading-tab-item.selected .tab-title {
        color: var(--reader-tabs-active-text);
    }

    .reading-tab-item.selected .tab-location {
        color: var(--reader-tabs-active-muted);
    }

    .reading-tab-item.collapsed {
        grid-template-columns: 1fr;
        justify-items: center;
        min-height: 2.4rem;
        padding: 0.27rem;
        border-radius: 0.62rem;
    }

    .reading-tab-item.collapsed.selected {
        grid-template-columns: calc(100% - var(--reader-tabs-inactive-end-gap, 0.45rem));
        justify-content: start;
        width: 100%;
        border-radius: 0.62rem 0 0 0.62rem;
    }

    .reading-tab-item.collapsed .tab-copy {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
    }

    @media only screen and (max-width: 1153px) {
        .reading-tab-item {
            min-height: 2.65rem;
            border-radius: 0.45rem;
        }

        .reading-tab-item.selected {
            border-radius: 0.45rem;
            box-shadow: none;
        }

        .reading-tab-item.selected::before {
            content: none;
        }

        .reading-tab-item.selected::after {
            content: "";
            top: auto;
            right: 0.55rem;
            bottom: -0.2rem;
            left: 0.55rem;
            width: auto;
            height: 0.18rem;
            border: 0;
            border-radius: 999px;
            box-shadow: none;
            background: var(--reader-tabs-active-border);
        }

        .reading-tab-item.collapsed {
            width: 2.65rem;
            min-height: 2.65rem;
            padding: 0.38rem;
        }
    }
</style>
